<template>
  <div>
    <button class="create-btn"
            @click="setShowModal"
            :content="'<p class=' + 'tooltip-shortcut' + '>' + $t('vue2.common.press') + '<span>N</span></p>'"
            v-tippy="{
                    arrow: true,
                    placement : 'bottom'
                  }">
      {{ $t('vue2.substitution.create_absence') }}
    </button>
    <transition name="fadeIn">
      <AppModal v-if="showModal"
                :modalResponsive="true"
                :isShowFooter="true"
                :modalFlexContainer="isShowSalaryInfoBox || false"
                @close="closeModal">
        <h2 slot="header">{{ $t('vue2.substitution.create_absence') }}</h2>
        <div slot="body" class="create-absence__form create-absence__form--flex" :class="{'create-absence__form--flex--opus-info-box': isShowSalaryInfoBox}">
          <div class="create-absence__form-group-container__left">
            <!-- ROW 1: HVEM -->
            <div class="create__form-group">
              <label>{{ $t('vue2.substitution.who') }}</label>
              <SubsCreateDSelect
                id="create_absence_user"
                :items="users"
                @update="setUsers">
              </SubsCreateDSelect>
              <span class="error-message" v-if="isAdditionalDataRequired && usersAbsent.length > 1">
                Der kan ikke oprettes fravær for flere medarbejdere på én gang, da denne årsag kræver ekstra information.
              </span>
              <span class="error-message"
                    v-if="!isUserSelected && showErrorMessages">{{ $t('vue2.substitution.person_must_be_filled') }}</span>
            </div>
            <!-- ROW 2: ÅRSAG -->
            <div class="create__form-group__flex-container">
              <div class="create__form-group">
                <label>{{ $t('vue2.substitution.reason') }}</label>
                <div class="absence-reason">
                  <dSelect :items="subsAbsenceReasonsSorted"
                          :receiveData="reasonCreated"
                          :multiple="false"
                          :placeholder="$t('vue2.substitution.choose_absence_reason')"
                          :itemSpaceBetween="true"
                          @update="setReason"
                          @searchText="setReasonSearchText"
                          @onSearchTextDebounced="onSearchTextDebounced"
                          @enter="isCreateAbsenceReason = true"
                          @close="onCloseDSelectAbsenceReasons">
                          <template #item-badge="{ item }" v-if="isAbsenceIntegrationActive">
                            <span class="do-opus-badge" v-if="item.kmd_absence_code_id">OPUS</span>
                            <span class="do-sd-badge" v-if="item.sd_absence_code_id">SD</span>
                          </template>
                    <div slot="noMatch">
                      <h4 class="create-absence-link" v-if="userHasPermission('can_manage_absence_reasons')"
                          @click="isCreateAbsenceReason = true">
                        + {{ $t('vue2.substitution.create') }}: "{{ reasonSearchText }}"
                      </h4>
                      <h4 v-else>
                        {{ $t('vue2.dselect.no_matching_results') }}
                      </h4>
                    </div>
                  </dSelect>
                  <div class="icon__bg-circle absence-reasons__settings"
                      v-if="userHasPermission('can_manage_absence_reasons') && isShowCreateAbsenceReasonButton"
                      v-tippy="{
                    arrow: true,
                    animateFill: false,
                    animation : 'shift-away',
                    zIndex: '999999992',
                    boundary: 'window'
                  }"
                      :content='$t("vue2.substitution.create") + ` "` + reasonSearchText + `" ` + i18n[locale]["as_a_new_reason"]'
                      @click="isCreateAbsenceReason = true">
                    <span class="d-icon-plus"></span>
                  </div>
                </div>
                <span class="error-message"
                      v-if="!isReasonSelected && showErrorMessages">{{
                    $t('vue2.substitution.reason_must_be_filled')
                  }}</span>
              </div>
            </div>
            <!-- ROW 3: DATO -->
            <div class="create__form-group">
              <label>{{ $t('vue2.substitution.date') }}</label>
              <div class="date-picker-range-group">
                <DatepickerRange
                  :allowSameDate="true"
                  :startdate="startDate"
                  :enddate="endDate"
                  @startchanged="setStartDate"
                  @endchanged="setEndDate">
                </DatepickerRange>
                <div class="absence-no-end-date" v-if="isOngoingAbsenceAllowed && !isShowSalaryInfoBox"
                     :class="{'absence-no-end-date--active': isAbsenceOngoing}">
                  <div class="absence-no-end-date__inner" @click.stop.prevent="setIsAbsenceOngoing(!isAbsenceOngoing)">
                    <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                    <label>{{ $t('vue2.subs_ongoing_absence.ongoing_absence') }}</label>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                  </DQuestionMark>
                </div>
                <div class="absence-no-end-date" v-else-if="!isOngoingAbsenceDateAllowed && !isShowSalaryInfoBox">
                  <p class="text-explainer">{{ $t('vue2.subs_ongoing_absence.cant_be_ongoing_absence') }}</p>
                </div>
              </div>
              <span class="error-message" v-if="isAbsenceOnlyOnWeekend">{{ i18n[locale]['absence_only_on_weekend'] }}</span>
              <div class="do-warning marg-1-top" v-if="absentUsersWithOngoingAbsenceAlready.length > 0">
                <b class="marg-05-right" v-for="user in absentUsersWithOngoingAbsenceAlready" :key="user">{{ user }} </b>
                {{ i18n[locale]['already_has_ongoing_absence'] }}
              </div>
            </div>
            <!-- ROW 4: TID -->
            <div class="create__form-group">
              <label>{{ $t('vue2.substitution.time') }}</label>
              <div class="time-changer__group">
                <TimeChanger
                  :showIcon="true"
                  :start="startTime"
                  :end="endTime"
                  :isFiveMinutesInterval="false"
                  @updateStart="setStartTime"
                  @updateEnd="setEndTime">
                </TimeChanger>
                <div class="hint--right" :aria-label="i18n[locale]['set_default_start_and_endtime']">
                  <div class="icon__bg-circle time-changer__settings" @click="isShowSettings = true">
                    <span class="icon-cog2"></span>
                  </div>
                </div>
              </div>
            </div>
            <!-- ROW 5: NOTE -->
            <div class="create__form-group create__form-group__note-to-absence">
              <label>{{ i18n[locale]['note_to_absence'] }}</label>
              <textarea class="d-input" v-model="noteForAbsentReason"></textarea>
            </div>
          </div>
          <div class="create-absence__form-group-container__right" v-if="isShowSalaryInfoBox">
            <div class="create-absence__salary-system-info-container" v-if="!isLoadedSalarySystemInfo">
              <div class="do-spinner"></div>
            </div>

            <!-- SD: hvad der senere indberettes til Silkeborg Data -->
            <div class="create-absence__salary-system-info" v-if="isLoadedSalarySystemInfo && isShowSdInfoBox">
              <div class="do-sd-badge do-opus-badge--large opus-info__header">SD</div>

              <!-- ROW 1: Ansættelsesnummer (ud for HVEM) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Ansættelse</label>
                <div class="users-employment-selection">
                  <div v-for="userCalendarId in usersAbsent" :key="userCalendarId" class="marg-1-bottom">
                    <label class="d-label marg-025-bottom" v-if="usersAbsent.length > 1">{{ getUserName(userCalendarId) }}</label>
                    <select
                      class="do-select"
                      v-model="selectedEmploymentIds[userCalendarId]"
                      @change="onEmploymentChange(userCalendarId, $event.target.value)">
                      <option value="">— Vælg ansættelsesnummer —</option>
                      <option
                        v-for="employment in getUserEmployments(userCalendarId)"
                        :key="employment.id"
                        :value="employment.id">
                        {{ employment.title }} - {{ employment.employment_number || employment.id }}
                      </option>
                    </select>
                  </div>
                  <span class="error-message"
                        v-if="!isEmploymentSelected && showErrorMessages">
                    Ansættelsesnummer skal vælges for alle medarbejdere
                  </span>
                </div>
              </div>

              <!-- ROW 2: Fraværsårsag med lønart (ud for ÅRSAG) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Fraværsårsag</label>
                <div class="opus-info__reason-display">
                  <span class="opus-info__reason-code" v-if="getSDAbsence">
                    {{ getSDAbsence.loenart }}{{ getSDAbsence.title ? ' - ' + getSDAbsence.title : '' }}
                  </span>
                </div>
              </div>

              <!-- ROW 3: Periode (ud for DATO) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Dato</label>
                <div class="opus-info__date-text">
                  <span v-if="isAbsenceOngoing">{{ startDateFormatted }} - Åbenstående</span>
                  <span v-else>{{ dateRangeFormatted }} <span class="opus-info__date-days">({{ dateRangeDaysFormatted }})</span></span>
                </div>
                <div v-if="isOngoingAbsenceAllowed" class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click.stop.prevent="setIsAbsenceOngoing(!isAbsenceOngoing)">
                    <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                    <span>Indberet som åbenstående</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                  </DQuestionMark>
                </div>
              </div>

              <!-- ROW 4: Tid (ud for TID) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Tid</label>
                <div class="opus-info__date-text" v-if="useTime">
                  {{ startTime.format('HH:mm') }} - {{ endTime.format('HH:mm') }} <span class="opus-info__date-days">({{ timeRangeHoursFormatted }})</span>
                </div>
                <div class="opus-info__date-text" v-else>
                  Hel dag
                </div>
                <div class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click="useTime = !useTime">
                    <app-toggle :prop-on="!useTime" @toggled="useTime = $event"></app-toggle>
                    <span>Indberet som hel dag</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">Toggle fra for at indberette timebaseret fravær, hvis fraværet kun gælder en del af dagen.</p>
                  </DQuestionMark>
                </div>
              </div>
            </div>

            <div class="create-absence__salary-system-info" v-if="isLoadedSalarySystemInfo && isShowOpusInfoBox">
              <div class="do-opus-badge do-opus-badge--large opus-info__header">Opus</div>

              <!-- ROW 1: Ansættelsesnummer (ud for HVEM) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Ansættelse</label>
                <div class="users-employment-selection">
                  <div v-for="userCalendarId in usersAbsent" :key="userCalendarId" class="marg-1-bottom">
                    <label class="d-label marg-025-bottom" v-if="usersAbsent.length > 1">{{ getUserName(userCalendarId) }}</label>
                    <select
                      class="do-select"
                      v-model="selectedEmploymentIds[userCalendarId]"
                      @change="onEmploymentChange(userCalendarId, $event.target.value)">
                      <option value="">— Vælg ansættelsesnummer —</option>
                      <option
                        v-for="employment in getUserEmployments(userCalendarId)"
                        :key="employment.id"
                        :value="employment.id">
                        {{ employment.title }} - {{ employment.employment_number || employment.id }}
                      </option>
                    </select>
                  </div>
                  <span class="error-message"
                        v-if="!isEmploymentSelected && showErrorMessages">
                    Ansættelsesnummer skal vælges for alle medarbejdere
                  </span>
                </div>
              </div>

              <!-- ROW 2: Fraværsårsag med lønart (ud for ÅRSAG) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Fraværsårsag</label>
                <div class="opus-info__reason-display">
                  <span class="opus-info__reason-code" v-if="getOPUSAbsence">
                    {{ getOPUSAbsence.absence_type }}{{ getOPUSAbsence.absence_type_text ? ' - ' + getOPUSAbsence.absence_type_text : '' }}
                  </span>
                </div>
                <div v-if="isShowExtraInformation" class="opus-info__extra-info">
                  <!-- GetMaternityCases / Barsel -->
                  <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'Maternity'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til barselssager</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.MaternityCaseKey" :value="option.MaternityCaseKey">
                        {{ option.ExpectedDeliveryDate }} - {{ option.MaternityCaseKey }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen barselssager tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <!-- GetReasons / Tjenestefri -->
                   <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'Reason'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til tjenestefri</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.ReasonCode" :value="option.ReasonCode">
                        {{ option.ReasonCodeText }} - {{ option.ReasonCode }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen tjenestefri årsager tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <!-- GetWorkAccidentCases / Arbejdsskade -->
                  <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'WorkInjury'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til arbejdsskader</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.AccidentNumber" :value="option.AccidentNumber">
                        {{ option.AccidentDate }} - {{ option.AccidentNumber }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen arbejdsskadesager tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <!-- GetChildren / Omsorgsdage -->
                  <template v-if="getOPUSAbsence && getOPUSAbsence.additional_data === 'Child'">
                    <label class="opus-info__label opus-info__label--secondary">Ekstra information til omsorgsdage</label>
                    <div class="do-spinner do-spinner--small" v-if="isLoadingAdditionalData"></div>
                    <div class="opus-info__error" v-else-if="additionalDataError">
                      <span class="opus-info__error-icon">⚠</span>
                      <span>Kunne ikke hente data fra Opus</span>
                    </div>
                    <select class="do-select" v-model="selectedAdditionalData" v-else-if="additionalDataOptions.length > 0">
                      <option value="">— Vælg ekstra information —</option>
                      <option v-for="option in additionalDataOptions" :key="option.ChildNumber" :value="option.ChildNumber">
                        {{ option.FirstName }} {{ option.LastName }} - {{ option.ChildNumber }}
                      </option>
                    </select>
                    <p class="opus-info__note" v-if="hasLoadedAdditionalData && additionalDataOptions.length === 0">
                      Der er ingen barn tilknyttet denne medarbejder i Opus.
                    </p>
                  </template>
                  <span class="error-message" v-if="!isAdditionalDataSelected && showErrorMessages">
                      {{ additionalDataValidationError }}
                    </span>
                </div>
              </div>

              <!-- ROW 3: Periode (ud for DATO) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Dato</label>
                <div class="opus-info__date-text">
                  <span v-if="isAbsenceOngoing">{{ startDateFormatted }} →</span>
                  <span v-else>{{ dateRangeFormatted }} <span class="opus-info__date-days">({{ dateRangeDaysFormatted }})</span></span>
                </div>
                <div v-if="isOngoingAbsenceAllowed" class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click.stop.prevent="setIsAbsenceOngoing(!isAbsenceOngoing)">
                    <app-toggle :prop-on="isAbsenceOngoing"></app-toggle>
                    <span>Indberet som åbenstående</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">{{ $t('vue2.subs_ongoing_absence.ongoing_absence_explained') }}</p>
                  </DQuestionMark>
                </div>
                <p class="opus-info__note--muted" v-else-if="!isOpusAllowOpen">
                  Åbenstående fravær er ikke muligt for denne fraværsårsag
                </p>
              </div>

              <!-- ROW 4: Hel dag (ud for TID) -->
              <div class="opus-info__field">
                <label class="opus-info__label">Tid</label>
                <div class="opus-info__date-text" v-if="useTime">
                  {{ startTime.format('HH:mm') }} - {{ endTime.format('HH:mm') }} <span class="opus-info__date-days">({{ timeRangeHoursFormatted }})</span>
                </div>
                <div class="opus-info__date-text" v-else>
                  Hel dag
                </div>
                <div class="opus-info__toggle-row opus-info__toggle-row--secondary">
                  <div class="do-opus-whole-day-toggle" @click="useTime = !useTime">
                    <app-toggle :prop-on="!useTime" @toggled="useTime = $event"></app-toggle>
                    <span>Indberet som hel dag</span>
                  </div>
                  <DQuestionMark>
                    <p slot="answer" class="text-dark">Toggle fra for at indberette timebaseret fravær, hvis fraværet kun gælder en del af dagen.</p>
                  </DQuestionMark>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div slot="footer">
          <button class="bttn create-btn right marg-10"
                  @click="submitUser"
                  :content="saveKeyTooltip"
                  v-tippy="{
                    placement : 'top',
                    zIndex: 12312312312312  
                  }">
            {{ $t('vue2.substitution.create_absence') }}
          </button>
        </div>
      </AppModal>
    </transition>
    <DModal v-if="isShowSettings" @close="isShowSettings = false">
      <div class="d-modal" slot="insertComponent" style="width: 400px">
        <div class="d-modal__header">
          <h2>{{ i18n[locale]['standard_setting'] }}</h2>
          <div class="d-modal__close icon__bg-circle" @click="isShowSettings = false">
            <span class="icon-cross2"></span>
          </div>
        </div>
        <div class="d-modal__content">
          <label class="d-input marg-1-bottom">{{ i18n[locale]['set_default_start_and_endtime'] }}</label>
          <div class="d-input-group-flex">
          <TimeChanger
            class="marg-1-top"
            :showIcon="true"
            :start="standardStartTime"
            :end="standardEndTime"
            :isFiveMinutesInterval="false"
            @updateStart="setStandardStartTime"
            @updateEnd="setStandardEndTime">
          </TimeChanger>
        </div>
        </div>
        <div class="d-modal__buttons pad-1">
          <button class="bttn bttn-simple marg-1-right" @click="isShowSettings = false">{{
              $t('vue2.common.cancel')
            }}
          </button>
          <button class="bttn submit-button"
                  @click="submitStandardSettings">
            {{ $t('vue2.common.save') }}
          </button>
        </div>
      </div>
    </DModal>
    <AbsenceReasonsCreate
      v-if="isCreateAbsenceReason"
      :initTitle="reasonSearchText"
      @create="createAbsenceReason"
      @cancel="isCreateAbsenceReason = false"
    />
  </div>
</template>

<script>
import AppModal from '../common/AppModal'
import DModal from '../common/DModal'
import dSelect from '../DSelect/DSelect'
import DatepickerRange from '../datepicker/DatePickerRange'
import TimeChanger from '../TimeChanger'
import DaySelector from './SubsCreateDayPicker'
import moment from 'moment'
import docendo from '../../docendo'
import eventbus from '../../eventbus'
import {mapGetters} from 'vuex'
import AppToggle from '../common/AppToggle'
import DQuestionMark from '../common/DQuestionMark'
import SubsCreateDSelect from './SubsCreateDSelect'
import AbsenceReasonsCreate from '../AbsenceReasons/AbsenceReasonsCreate.vue'

export default {
  components: {
    AbsenceReasonsCreate,
    SubsCreateDSelect,
    AppToggle, AppModal, DModal, dSelect, DatepickerRange, TimeChanger, DaySelector, DQuestionMark
  },
  props: {
    users: {
      type: Array,
      default: () => []
    },
    usersWithOngoingAbsenceInHash: {
      type: Object,
      default: () => {
      }
    },
    allUsersInCalIdHash: {
      type: Object,
      default: () => {
      }
    },
    schoolId: {
      default: null
    }
  },
  computed: {
    ...mapGetters([
      'subsAbsenceReasonsSorted',
      'locale',
      'employmentsInUserIdHash'
    ]),
    getOPUSAbsence () {
      if (!this.reason || !this.reason.kmd_absence_code_id) {
        return null
      }
      const opusAbsenceId = this.reason.kmd_absence_code_id
      const absenceCodes = this.opusAbsenceCodes || []
      if (!Array.isArray(absenceCodes) || absenceCodes.length < 1) {
        return null
      }
      const match = absenceCodes.find(code => String(code.id) === String(opusAbsenceId))
      return match || null
    },
    getSDAbsence () {
      if (!this.reason || !this.reason.sd_absence_code_id) {
        return null
      }
      const sdAbsenceId = this.reason.sd_absence_code_id
      const absenceCodes = this.sdAbsenceCodes || []
      if (!Array.isArray(absenceCodes) || absenceCodes.length < 1) {
        return null
      }
      const match = absenceCodes.find(code => String(code.id) === String(sdAbsenceId))
      return match || null
    },
    timeRangeHours () {
      if (!this.startTime || !this.endTime) {
        return 0
      }
      const diffMinutes = this.endTime.diff(this.startTime, 'minutes')
      const hours = diffMinutes / 60
      return Math.round(hours * 100) / 100 // Afrund til 2 decimaler
    },
    timeRangeHoursFormatted () {
      const hours = this.timeRangeHours
      if (hours === 1) {
        return '1 time'
      }
      // Formater med komma i stedet for punktum
      return String(hours).replace('.', ',') + ' timer'
    },
    dateRangeDays () {
      if (!this.startDate || !this.endDate) {
        return 1
      }
      const start = moment(this.startDate).startOf('day')
      const end = moment(this.endDate).startOf('day')
      return end.diff(start, 'days') + 1 // +1 for at inkludere både start og slutdato
    },
    dateRangeDaysFormatted () {
      const days = this.dateRangeDays
      if (days === 1) {
        return '1 dag'
      }
      return days + ' dage'
    },
    dateRangeFormatted () {
      const start = moment(this.startDate).format('DD-MM-YYYY')
      const end = moment(this.endDate).format('DD-MM-YYYY')
      if (start === end) {
        return start
      }
      return start + ' - ' + end
    },
    startDateFormatted () {
      return moment(this.startDate).format('DD-MM-YYYY')
    },
    isOpusAllowOpen () {
      if (!this.isOpusAbsenceActivated || !this.reason || !this.reason.kmd_absence_code_id) {
        return true
      }
      const opusAbsence = this.getOPUSAbsence
      if (!opusAbsence || typeof opusAbsence.allow_open === 'undefined') {
        return true
      }
      return opusAbsence.allow_open === true
    },
    isOpusAbsenceActivated () {
      return this.$store.getters.schoolFeatures && this.$store.getters.schoolFeatures.indexOf('kmd_absence') > -1
    },
    isSDIntegrationActive () {
      return this.$store.getters.schoolFeatures && this.$store.getters.schoolFeatures.indexOf('silkeborg_data_integration') > -1
    },
    isAbsenceIntegrationActive () {
      return this.isOpusAbsenceActivated || this.isSDIntegrationActive
    },
    isShowOpusInfoBox () {
      return this.isOpusAbsenceActivated && this.reason && this.reason.kmd_absence_code_id && this.usersAbsent.length > 0
    },
    isShowSdInfoBox () {
      return this.isSDIntegrationActive && this.reason && this.reason.sd_absence_code_id && this.usersAbsent.length > 0
    },
    isShowSalaryInfoBox () {
      return this.isShowOpusInfoBox || this.isShowSdInfoBox
    },
    isShowExtraInformation () {
      const opusAbsence = this.getOPUSAbsence
      if (!opusAbsence || !opusAbsence.additional_data) { // Når vi skal have årsager på Tjenestetid
        return false
      }
      // Tjek at mindst én employment er valgt
      const firstUserCalendarId = this.usersAbsent[0]
      return firstUserCalendarId && this.selectedEmploymentIds[firstUserCalendarId]
    },
    shouldShowEmploymentSelection () {
      return this.isAbsenceIntegrationActive && this.reason && ((this.reason.kmd_absence_code_id && this.isOpusAbsenceActivated) || (this.reason.sd_absence_code_id && this.isSDIntegrationActive)) && this.usersAbsent.length > 0
    },
    isEmploymentSelected () {
      if (!this.shouldShowEmploymentSelection) {
        return true
      }
      return this.usersAbsent.every(userCalendarId => {
        return this.selectedEmploymentIds[userCalendarId] && this.selectedEmploymentIds[userCalendarId] !== ''
      })
    },
    absentUsersWithOngoingAbsenceAlready () {
      if (!this.isAbsenceOngoing) {
        return []  // Early return if absence isn't ongoing
      }

      // Filter users with ongoing absence and map their names (or IDs if name is unavailable)
      return this.usersAbsent
        .filter(absentUserCalId => this.usersWithOngoingAbsenceInHash[absentUserCalId]) // Only keep users with ongoing absence
        .map(absentUserCalId => {
          const user = this.allUsersInCalIdHash[absentUserCalId]
          return user ? user.name : absentUserCalId // Return user name or ID if name isn't found
        })
    },
    text () {
      return ''
    },
    user () {
      return this.$store.state.user
    },
    isUserLoaded () {
      return this.$store.state.isUserLoaded
    },
    isPermissionsActivatedForSchool () {
      return this.$store.getters.isPermissionsActivatedForSchool
    },
    isAdditionalDataRequired () {
      return this.getOPUSAbsence && this.getOPUSAbsence.additional_data_required
    },
    isAdditionalDataSelected () {
      if (!this.isAdditionalDataRequired) {
        return true
      }
      return this.selectedAdditionalData !== ''
    },
    userPermissions () {
      return this.$store.getters.userPermissions
    },
    dbEventStart () {
      let date = moment(this.startDate).format('YYYY-MM-DD')
      let time = this.startTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    dbEventEnd () {
      let date = moment(this.endDate).format('YYYY-MM-DD')
      let time = this.endTime.locale('LT')
      return moment(date).set({
        hour: time.get('hour'),
        minutes: time.get('minutes')
      })
    },
    isUserSelected () {
      return this.usersAbsent.length > 0
    },
    isReasonSelected () {
      return this.reason !== ''
    },
    getDate () {
      return this.$store.state.subs.date
    },
    isOngoingAbsenceDateAllowed () {
      let endDate = moment(this.endDate).format('YYYY-MM-DD')
      let now = moment().format('YYYY-MM-DD')
      return moment(endDate).isSameOrAfter(now)
    },
    isAbsenceOnlyOnWeekend () {
      if (this.isAbsenceOngoing || !this.startDate || !this.endDate) {
        return false
      }
      const start = moment(this.startDate).startOf('day')
      const end = moment(this.endDate).startOf('day')
      for (let date = start.clone(); date.isSameOrBefore(end); date.add(1, 'day')) {
        if (date.isoWeekday() < 6) {
          return false
        }
      }
      return true
    },
    isOngoingAbsenceAllowed () {
      return this.isOngoingAbsenceDateAllowed && this.isOpusAllowOpen
    },
    userSettings () {
      return this.$store.state.userSettings
    },
    metaKey () {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      return isMac ? '⌘' : 'CTRL'
    },
    saveKeyTooltip () {
      return '<p class="tooltip-shortcut tooltip-shortcut--double-key"><span>' + this.metaKey + '</span>' + '<span>Enter</span></p>'
    }
  },
  data () {
    return {
      isLoadedSalarySystemInfo: false,
      useTime: false,
      useTimeBeforeTimeEdit: false,
      wholeDayToastTimer: null,
      isCreateAbsenceReason: false,
      i18n: {
        da: {
          note_to_absence: 'Eventuel note til fraværet',
          set_default_start_and_endtime: 'Sæt standard start- og sluttid',
          standard_setting: 'Standard indstilling',
          as_a_new_reason: 'som ny årsag',
          already_has_ongoing_absence: 'har allerede åbenstående fravær',
          absence_only_on_weekend: 'Fravær kan ikke oprettes i weekenden. Vælg en periode, der indeholder mindst én hverdag.'
        },
        en: {
          note_to_absence: 'Note to absence',
          set_default_start_and_endtime: 'Set default start and end time',
          standard_setting: 'Standard setting',
          as_a_new_reason: 'as a new absence',
          already_has_ongoing_absence: 'already has ongoing absence',
          absence_only_on_weekend: 'Absence cannot be created on weekends. Choose a period that includes at least one weekday.'
        },
        nb: {
          note_to_absence: 'Merknad til fravær',
          set_default_start_and_endtime: 'Angi standard start- og sluttid',
          standard_setting: 'Standardinnstilling',
          as_a_new_reason: 'som en ny årsak',
          already_has_ongoing_absence: 'har allerede pågående fravær',
          absence_only_on_weekend: 'Fravær kan ikke opprettes i helgen. Velg en periode som inneholder minst én ukedag.'
        }
      },
      noteForAbsentReason: '',
      showModal: false,
      showErrorMessages: false,
      usersAbsent: [],
      startDate: moment(),
      endDate: moment(),
      reason: '',
      reasonCreated: '',
      startTime: moment().set({'hour': 8, 'minutes': 0}),
      endTime: moment().set({'hour': 16, 'minutes': 0}),
      standardStartTime: moment().set({'hour': 8, 'minutes': 0}),
      standardEndTime: moment().set({'hour': 16, 'minutes': 0}),
      isAbsenceOngoing: false,
      isShowSettings: false,
      reasonSearchText: '',
      isShowCreateAbsenceReasonButton: false,
      isEventListenerForSubmitAdded: false,
      isEndDateManipulatedFromDev: false,
      originalEndDate: null,
      selectedEmploymentIds: {},
      opusAbsenceCodes: [],
      sdAbsenceCodes: [],
      additionalDataOptions: [],
      selectedAdditionalData: null,
      isLoadingAdditionalData: false,
      hasLoadedAdditionalData: false,
      additionalDataError: null
    }
  },
  methods: {
    setIsAbsenceOngoing (bool) {
      if (!this.isOpusAllowOpen && bool) {
        this.isAbsenceOngoing = false
        return
      }
      this.isAbsenceOngoing = bool
      if (bool) {
        const start = moment(this.startDate).format('YYYY-MM-DD')
        const end = moment(this.endDate).format('YYYY-MM-DD')
        this.originalEndDate = end
        // Gem om den oprindelige slutdato er en fredag
        const endWasFriday = moment(end).isoWeekday() === 5
        let newEnd = end
        // Hvis start og slut er samme dag, så læg 1 dag til slutdato
        if (start === end) {
          newEnd = moment(end).add(1, 'day')
          this.isEndDateManipulatedFromDev = true
        }
        // Hvis slutdatoen (oprindeligt valgt) var fredag, så sørg for at mandag også er med
        if (endWasFriday) {
          newEnd = moment(end).add(3, 'days') // fredag -> mandag
          this.isEndDateManipulatedFromDev = true
        }
        this.endDate = newEnd
      } else {
        // Slås den fra, gør vi ikke andet end at sikre flaget er falsk
        // (redigerings-tilfældet er dækket af linjen øverst)
        if (this.isEndDateManipulatedFromDev) {
          this.endDate = moment(this.originalEndDate).format('YYYY-MM-DD')
          this.isEndDateManipulatedFromDev = false
        }
      }
    },
    onCloseDSelectAbsenceReasons () {
      this.isShowCreateAbsenceReasonButton = false
    },
    setIsShowCreateAbsenceReasonButton () {
      // Tjek mod originale navne (uden OPUS label)
      const idx = this.subsAbsenceReasonsSorted.findIndex(absenceReason => absenceReason.name === this.reasonSearchText)
      this.isShowCreateAbsenceReasonButton = this.reasonSearchText.length > 0 && idx < 0
    },
    onSearchTextDebounced () {
      this.setIsShowCreateAbsenceReasonButton()
    },
    setIsAbsenceRunning (val) {
      if (this.isAbsenceOngoing !== val) {
        this.isAbsenceOngoing = !this.isAbsenceOngoing
      }
    },
    userHasPermission (val) {
      if (!this.user) {
        return false
      }
      if (this.isPermissionsActivatedForSchool) {
        return this.userPermissions.indexOf(val) > -1 || this.user.admin
      } else {
        return true
      }
    },
    setShowModal () {
      this.showModal = true
      this.startDate = moment(this.getDate)
      this.endDate = moment(this.getDate)
      this.addEventListenerSubmit()
      if (this.userSettings) {
        if (this.userSettings.subSettingsCreateTime) {
          let start = moment(this.userSettings.subSettingsCreateTime.start)
          let end = moment(this.userSettings.subSettingsCreateTime.end)
          this.startTime = moment().set({'hour': moment(start).hour(), 'minutes': moment(start).minute()})
          this.endTime = moment().set({'hour': moment(end).hour(), 'minutes': moment(end).minute()})
          this.standardStartTime = this.startTime
          this.standardEndTime = this.endTime
        }
      }
      setTimeout(_ => {
        document.querySelector('#create_absence_user .d-select__input-look > input[type="search"]').focus()
      }, 500)
    },
    addEventListenerSubmit () {
      if (!this.isEventListenerForSubmitAdded) {
        window.addEventListener('keydown', this.onHandleKeyboardShortcutSubmit)
        window.addEventListener('keydown', this.onHandleKeyboardShortcutEsc)
        this.isEventListenerForSubmitAdded = true
      }
    },
    removeEventListenerForSubmit () {
      window.removeEventListener('keydown', this.onHandleKeyboardShortcutSubmit)
      window.removeEventListener('keydown', this.onHandleKeyboardShortcutEsc)
      this.isEventListenerForSubmitAdded = false
    },
    closeModal () {
      this.showModal = false
      this.showErrorMessages = false
      this.isAbsenceOngoing = false
      this.useTime = false
      this.reason = ''
      this.reasonSearchText = ''
      this.isShowCreateAbsenceReasonButton = false
      this.selectedEmploymentIds = {}
      this.usersAbsent = []
      if (this.isEndDateManipulatedFromDev) {
        this.endDate = moment(this.originalEndDate)
      }
      this.isEndDateManipulatedFromDev = false
      this.originalEndDate = null
      this.removeEventListenerForSubmit()
    },
    createAbsenceReason (titleAndDefaultPrivate) {
      const title = titleAndDefaultPrivate.title
      const defaultPrivate = titleAndDefaultPrivate.defaultPrivate
      const canRequest = true
      const defaultOngoing = false
      const kmdAbsenceCodeId = null
      const sdAbsenceCodeId = null
      const plannedAbsence = false
      docendo.schoolAbsenceSubjectsCreate(this.schoolId, title, '#564437', defaultPrivate, defaultOngoing, plannedAbsence, kmdAbsenceCodeId, sdAbsenceCodeId, canRequest, data => {
        this.$store.commit('subsAddNewAbsenceReason', data)
        this.reasonCreated = data
        this.isCreateAbsenceReason = false
      }, failure => {
        console.log('Failed', failure)
      })
    },
    setReasonSearchText (text) {
      this.reasonSearchText = text
    },
    setUsers (users) {
      const newUsersAbsent = users.map(user => user.user_calendar_id)
      // Bevar valgte employment numbers for personer der stadig er valgt
      const preservedEmploymentIds = {}
      newUsersAbsent.forEach(userCalendarId => {
        if (this.selectedEmploymentIds[userCalendarId]) {
          preservedEmploymentIds[userCalendarId] = this.selectedEmploymentIds[userCalendarId]
        }
      })
      this.usersAbsent = newUsersAbsent
      this.selectedEmploymentIds = preservedEmploymentIds
      // Auto-vælg ansættelse hvis årsag har kmd_absence_code_id eller sd_absence_code_id
      if (this.reason && (this.reason.kmd_absence_code_id || this.reason.sd_absence_code_id)) {
        this.autoSelectSingleEmployments()
      }
    },
    setReason (reason) {
      this.reason = reason[0]
      const shouldBeOngoing = !!(reason[0] && reason[0].default_ongoing && this.isOpusAllowOpen)
      if (shouldBeOngoing !== this.isAbsenceOngoing) {
        this.setIsAbsenceOngoing(shouldBeOngoing)
      }
      // Nulstil ansættelsesnummer valg når årsag ændres
      if (!reason[0] || (!reason[0].kmd_absence_code_id && !reason[0].sd_absence_code_id)) {
        this.selectedEmploymentIds = {}
      } else {
        // Auto-vælg ansættelse hvis årsag har kmd_absence_code_id eller sd_absence_code_id
        this.autoSelectSingleEmployments()
      }
    },
    getUserName (userCalendarId) {
      const user = this.allUsersInCalIdHash[userCalendarId]
      return user ? user.name : userCalendarId
    },
    getUserEmployments (userCalendarId) {
      const user = this.allUsersInCalIdHash[userCalendarId]
      if (!user || !user.id) {
        return []
      }
      const employments = this.employmentsInUserIdHash[user.id] || []
      // Filtrer kun employments med employment_number
      return employments.filter(emp => emp.employment_number && String(emp.employment_number).trim() !== '')
    },
    getEmploymentNumber (userCalendarId) {
      const employmentId = this.selectedEmploymentIds[userCalendarId]
      if (!employmentId) {
        return '—'
      }
      const employments = this.getUserEmployments(userCalendarId)
      const match = employments.find(emp => emp.id === Number(employmentId))
      if (!match) {
        return '—'
      }
      return match.employment_number || match.id
    },
    onEmploymentChange (userCalendarId, employmentId) {
      this.$set(this.selectedEmploymentIds, userCalendarId, employmentId)
    },
    autoSelectSingleEmployments () {
      if (!this.shouldShowEmploymentSelection) {
        return
      }
      this.usersAbsent.forEach(userCalendarId => {
        // Hvis der allerede er valgt en ansættelse for denne bruger, spring over
        if (this.selectedEmploymentIds[userCalendarId]) {
          return
        }
        const employments = this.getUserEmployments(userCalendarId)
        // Hvis der kun er én ansættelse, vælg den automatisk
        if (employments.length === 1) {
          this.$set(this.selectedEmploymentIds, userCalendarId, employments[0].id)
        }
      })
    },
    setStartDate (payload) {
      this.startDate = payload
    },
    setEndDate (payload) {
      this.endDate = payload
      const endDate = moment(payload).format('YYYY-MM-DD')
      const now = moment().format('YYYY-MM-DD')
      if (this.isAbsenceOngoing && !moment(endDate).isAfter(now)) {
        this.isAbsenceOngoing = false
        this.isEndDateManipulatedFromDev = false
      }
    },
    setStartTime (payload) {
      this.startTime = payload
      this.syncWholeDayWithStandardTimes()
    },
    setEndTime (payload) {
      this.endTime = payload
      this.syncWholeDayWithStandardTimes()
    },
    syncWholeDayWithStandardTimes () {
      const saved = this.userSettings && this.userSettings.subSettingsCreateTime
      const standardStart = saved ? moment(saved.start).format('HH:mm') : '08:00'
      const standardEnd = saved ? moment(saved.end).format('HH:mm') : '16:00'
      const isStandardStart = moment(this.startTime).format('HH:mm') === standardStart
      const isStandardEnd = moment(this.endTime).format('HH:mm') === standardEnd
      if (this.wholeDayToastTimer === null) {
        this.useTimeBeforeTimeEdit = this.useTime
      }
      this.useTime = !(isStandardStart && isStandardEnd)
      this.notifyWholeDayChange()
    },
    notifyWholeDayChange () {
      clearTimeout(this.wholeDayToastTimer)
      this.wholeDayToastTimer = setTimeout(() => {
        this.wholeDayToastTimer = null
        if (this.isShowSalaryInfoBox && this.useTime !== this.useTimeBeforeTimeEdit) {
          eventbus.toast = {text: this.useTime ? '"Indberet som hel dag" er slået fra' : '"Indberet som hel dag" er slået til', success: true}
        }
      }, 800)
    },
    setStandardStartTime (payload) {
      this.standardStartTime = payload
    },
    setStandardEndTime (payload) {
      this.standardEndTime = payload
    },
    submitUser () {
      if (!this.isUserSelected || !this.isReasonSelected) {
        this.showErrorMessages = true
        return
      }
      if (this.shouldShowEmploymentSelection && !this.isEmploymentSelected) {
        this.showErrorMessages = true
        return
      }
      if (!this.isAdditionalDataSelected) {
        this.showErrorMessages = true
        this.additionalDataValidationError = 'Du skal vælge ekstra information'
        return
      }
      if (this.isAdditionalDataRequired && this.usersAbsent.length > 1) {
        return
      }
      if (this.isAbsenceOnlyOnWeekend) {
        return
      }
      if (!this.isOngoingAbsenceAllowed) {
        this.isAbsenceOngoing = false
      }
      let currDate = moment(this.getDate).format('YYYY-MM-DD')
      let absenceStart = moment(this.startDate).format('YYYY-MM-DD')
      let absenceEnd = moment(this.endDate).format('YYYY-MM-DD')
      let absenceOverlapsCurrentDate = moment(currDate).isBetween(absenceStart, absenceEnd, null, [])
      const startTime = this.startTime.second(0).millisecond(0).toISOString()
      const endTime = this.endTime.second(0).millisecond(0).toISOString()
      const useTime = this.useTime
      this.usersAbsent.forEach(userCalendarId => {
        const employmentId = this.shouldShowEmploymentSelection ? this.selectedEmploymentIds[userCalendarId] : null
        const additionalData = this.selectedAdditionalData || null
        docendo.schoolEventSerialsCreate(window.SCHOOL.id, userCalendarId, this.reason.id, absenceStart, absenceEnd, startTime, endTime, this.isAbsenceOngoing, this.noteForAbsentReason, employmentId, useTime, additionalData, data => {
          let parsedEvent = JSON.parse(data)
          // Push on page if absenceIsOnCurrentDate
          const viewedDate = moment(this.getDate).format('YYYY-MM-DD')
          if (absenceOverlapsCurrentDate && viewedDate === currDate && parsedEvent && parsedEvent.events) {
            const todayEvent = parsedEvent.events.find(event => moment(event.start).format('YYYY-MM-DD') === currDate)
            if (todayEvent) {
              this.$store.commit('updateEventOnAllEventsInAHash', todayEvent)
              this.$store.commit('addAbsenceToUserInAllUsersWithEvents', {
                cal_id: parsedEvent.calendar_id,
                event_id: todayEvent.id
              })
            }
          }
          this.$store.dispatch('subsGetLongAbsenceList')
        }, failure => {
          console.log('Failed to create absence:', failure)
        })
      })
      this.showModal = false
      this.showErrorMessages = false
      this.clearForm()
      this.$emit('created')
    },
    clearForm () {
      this.startDate = moment(this.getDate)
      this.endDate = moment(this.getDate)
      this.startTime = moment().set({'hour': 8, 'minutes': 0})
      this.endTime = moment().set({'hour': 16, 'minutes': 0})
      this.reason = ''
      this.noteForAbsentReason = ''
      this.showErrorMessages = false
      this.additionalDataValidationError = null
      this.selectedEmploymentIds = {}
      this.isAbsenceOngoing = false
      this.useTime = false
    },
    submitStandardSettings () {
      let standardSubsCreateTime = {'subSettingsCreateTime': {start: this.standardStartTime, end: this.standardEndTime}}
      this.$store.dispatch('updateUserSettings', standardSubsCreateTime)
      let start = this.standardStartTime
      let end = this.standardEndTime
      this.startTime = moment().set({'hour': moment(start).hour(), 'minutes': moment(start).minute()})
      this.endTime = moment().set({'hour': moment(end).hour(), 'minutes': moment(end).minute()})
      this.syncWholeDayWithStandardTimes()
      this.isShowSettings = false
    },
    onHandleKeyboardShortcutSubmit (e) {
      // Check if the Ctrl, Alt, and K keys are pressed
      if (this.showModal && (e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        this.submitUser()
      }
    },
    onHandleKeyboardShortcutEsc (e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.closeModal()
      }
    },
    listKmdOpusAbsenceCodes () {
      docendo.listKmdOpusAbsenceCodes(this.schoolId, success => {
        this.opusAbsenceCodes = Array.isArray(success) ? success : []
      }, failure => {
        this.opusAbsenceCodes = []
      })
    },
    listSdAbsenceCodes () {
      docendo.listSilkeborgDataAbsenceCodes(this.schoolId, success => {
        this.sdAbsenceCodes = Array.isArray(success) ? success : (success && Array.isArray(success.data) ? success.data : [])
      }, failure => {
        this.sdAbsenceCodes = []
      })
    },
    resetOpusFields () {
      // Nulstil alle OPUS-relaterede felter
      this.useTime = false
      this.additionalDataOptions = []
      this.selectedAdditionalData = null
      this.hasLoadedAdditionalData = false
      this.additionalDataError = null
      this.additionalDataValidationError = null
    },
    fetchAdditionalDataForAbsence () {
      console.log('fetchAdditionalDataForAbsence')
      // Hent ekstra data når employment og årsag er valgt
      if (!this.isShowExtraInformation) {
        this.additionalDataOptions = []
        this.selectedAdditionalData = null
        return
      }
      const opusAbsence = this.getOPUSAbsence
      if (!opusAbsence) return

      // Find første valgte employment
      const firstUserCalendarId = this.usersAbsent[0]
      const employmentId = this.selectedEmploymentIds[firstUserCalendarId]
      if (!employmentId) return

      this.isLoadingAdditionalData = true
      this.hasLoadedAdditionalData = false
      this.additionalDataOptions = []
      this.selectedAdditionalData = null

      console.log('Calling listAdditionalDataForAbsence', {
        schoolId: this.schoolId,
        employmentId: employmentId,
        absenceCodeId: opusAbsence.id
      })

      docendo.listAdditionalDataForAbsence(
        this.schoolId,
        employmentId,
        opusAbsence.id,
        moment(this.startDate).format('YYYY-MM-DD'),
        moment(this.endDate).format('YYYY-MM-DD'),
        response => {
          console.log('fetchAdditionalDataForAbsence response', response)
          // Tjek for fejl i svaret - kan komme i flere formater
          if (response && response.success === false) {
            this.additionalDataOptions = []
            this.additionalDataError = (response.data && response.data.error) || response.error || 'Der opstod en fejl'
            this.isLoadingAdditionalData = false
            this.hasLoadedAdditionalData = true
            return
          }
          if (response && response.error) {
            this.additionalDataOptions = []
            this.additionalDataError = response.error
            this.isLoadingAdditionalData = false
            this.hasLoadedAdditionalData = true
            return
          }
          if (response && response.data && response.data.error) {
            this.additionalDataOptions = []
            this.additionalDataError = response.data.error
            this.isLoadingAdditionalData = false
            this.hasLoadedAdditionalData = true
            return
          }
          this.additionalDataError = null
          // Parse response.response hvis det er en JSON-streng
          let parsedData = response
          if (response && response.response && typeof response.response === 'string') {
            try {
              parsedData = JSON.parse(response.response)
            } catch (e) {
              console.error('Failed to parse response.response', e)
            }
          }
          // Svarformat: { Body: { GetMaternityCasesResponse_MT: { MaternityCase: null | { Item: ... } | array } } }
          let cases = null
          if (parsedData && parsedData.Body) {
            if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'Maternity') {
              cases = parsedData.Body.GetMaternityCasesResponse_MT && parsedData.Body.GetMaternityCasesResponse_MT.MaternityCase
            } else if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'Reason') {
              const reasonsResponse = parsedData.Body.GetReasonsResponse_MT || parsedData.Body.GetReasonsResponse_RT
              cases = reasonsResponse && reasonsResponse.Reason
            } else if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'WorkInjury') {
              cases = parsedData.Body.GetWorkAccidentCasesResponse_MT && parsedData.Body.GetWorkAccidentCasesResponse_MT.AccidentCase
            } else if (this.getOPUSAbsence && this.getOPUSAbsence.additional_data === 'Child') {
              cases = parsedData.Body.GetChildrenResponse_MT && parsedData.Body.GetChildrenResponse_MT.Child
            }
          }
          // Håndter forskellige response formater: array, objekt med Item, eller direkte objekt
          if (Array.isArray(cases)) {
            this.additionalDataOptions = cases
          } else if (cases && cases.Item) {
            // Hvis data er wrappet i Item (enkelt element)
            this.additionalDataOptions = Array.isArray(cases.Item) ? cases.Item : [cases.Item]
          } else if (cases && typeof cases === 'object') {
            // Hvis det er et enkelt objekt uden Item wrapper
            this.additionalDataOptions = [cases]
          } else {
            this.additionalDataOptions = []
          }
          // Auto-vælg hvis der kun er ét resultat
          if (this.additionalDataOptions.length === 1) {
            var singleOption = this.additionalDataOptions[0]
            var additionalDataType = this.getOPUSAbsence && this.getOPUSAbsence.additional_data
            if (additionalDataType === 'Maternity' && singleOption.MaternityCaseKey) {
              this.selectedAdditionalData = singleOption.MaternityCaseKey
            } else if (additionalDataType === 'Reason' && singleOption.ReasonCode) {
              this.selectedAdditionalData = singleOption.ReasonCode
            } else if (additionalDataType === 'WorkInjury' && singleOption.AccidentNumber) {
              this.selectedAdditionalData = singleOption.AccidentNumber
            } else if (additionalDataType === 'Child' && singleOption.ChildNumber) {
              this.selectedAdditionalData = singleOption.ChildNumber
            }
          }
          this.isLoadingAdditionalData = false
          this.hasLoadedAdditionalData = true
        },
        error => {
          console.error('fetchAdditionalDataForAbsence error', error)
          this.additionalDataOptions = []
          // Hent fejlbesked fra forskellige mulige steder i error objektet
          if (error && error.data && error.data.error) {
            this.additionalDataError = error.data.error
          } else if (error && error.error) {
            this.additionalDataError = error.error
          } else {
            this.additionalDataError = 'Der opstod en fejl ved hentning af data'
          }
          this.isLoadingAdditionalData = false
          this.hasLoadedAdditionalData = true
        }
      )
    }
  },
  watch: {
    employmentsInUserIdHash () {
      // Auto-vælg ansættelse når employments indlæses
      if (this.shouldShowEmploymentSelection) {
        this.$nextTick(() => {
          this.autoSelectSingleEmployments()
        })
      }
    },
    reason (newValue, oldValue) {
      // Nulstil OPUS-felter når årsag ændres
      if (newValue !== oldValue) {
        this.resetOpusFields()
      }
    },
    usersAbsent: {
      handler (newValue, oldValue) {
        // Nulstil employment-valg når personer ændres
        if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          this.selectedEmploymentIds = {}
          this.resetOpusFields()
          // Auto-vælg ansættelse for nye brugere
          if (this.shouldShowEmploymentSelection) {
            this.$nextTick(() => {
              this.autoSelectSingleEmployments()
            })
          }
        }
      },
      deep: true
    },
    isShowSalaryInfoBox (newValue) {
      if (newValue) {
        this.isLoadedSalarySystemInfo = false
        setTimeout(() => {
          this.isLoadedSalarySystemInfo = true
        }, 350)
      } else {
        this.useTime = false
        this.isLoadedSalarySystemInfo = false
      }
    },
    useTime (nextValue) {
      if (!nextValue) {
        return
      }
      this.$nextTick(() => {
        if (document.activeElement && document.activeElement.classList.contains('d-time-change-input')) {
          return
        }
        const el = this.$el.querySelector('.d-time-change-input')
        if (el) {
          el.focus()
        }
      })
    },
    isShowExtraInformation (newValue) {
      if (newValue) {
        this.fetchAdditionalDataForAbsence()
      } else {
        this.additionalDataOptions = []
        this.selectedAdditionalData = null
        this.hasLoadedAdditionalData = false
        this.additionalDataError = null
      }
    }
  },
  mounted () {
    if (this.isAbsenceIntegrationActive) {
      this.$store.dispatch('readEmployments')
      if (this.isOpusAbsenceActivated) this.listKmdOpusAbsenceCodes()
      if (this.isSDIntegrationActive) this.listSdAbsenceCodes()
    }
    this.$store.dispatch('subsGetAbsenceReasons')
    this._keyListener = function (e) {
      // if (e.key === 'n' && (e.ctrlKey || e.metaKey)) {
      if (e.key === 'n') {
        if ((e.target.tagName.toUpperCase() === 'BODY')) {
          // Stop tastetrykket, så genvejens "n" ikke lander i det
          // inputfelt, der får fokus, når modalen åbner.
          e.preventDefault()
          this.setShowModal()
        }
      }
    }
    document.addEventListener('keydown', this._keyListener.bind(this))
  },
  beforeDestroy () {
    this.removeEventListenerForSubmit()
    document.removeEventListener('keydown', this._keyListener)
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.create-absence__form-group-container__left {
  padding: 2rem;
  width: 100%;
  max-width: 100vw;
}

.create-absence__form--flex--opus-info-box .create-absence__form-group-container__left {
  width: 600px;
}

.create-absence__form-group-container__right {
  background-color: #fefce8;
  max-width: 300px;
  width: 100%;
  padding: 1.5rem 1rem;
  flex: 1;
  border-left: 1px solid #facc17;
  position: relative;
}

@media (max-width: 1050px) {
  .create-absence__form-group-container__right {
    max-width: 100%;
  }
}

.create-absence__salary-system-info-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.opus-info__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: #713f12;
  margin: 0 0 1.25rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #fde047;
}

.opus-info__field {
  margin-bottom: 1rem;
}

.opus-info__label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.5rem;
}

.opus-info__toggle-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.opus-info__toggle-row .do-opus-whole-day-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
    padding: 0.25rem 1rem;
    border-radius: 4px;
    flex-direction: row-reverse;
    justify-content: space-between;
    width: 100%;
    box-shadow: 0 0 0 0 transparent, 0 0 0 0 transparent, 0 1px 1px 0 rgba(0, 0, 0, .12), 0 0 0 1px rgba(64, 68, 82, .16), 0 0 0 0 transparent, 0 0 0 0 transparent, 0 2px 5px 0 rgba(64, 68, 82, .08);
    background: #fff;
}

.do-opus-whole-day-toggle label {
  margin-bottom: 0;
  cursor: pointer;
}

.opus-info__toggle-row .do-opus-whole-day-toggle span {
  font-size: 0.875rem;
  color: #78350f;
}

.opus-info__note {
  font-size: 0.875rem;
  color: #92400e;
  font-style: italic;
  margin: 0;
}

.opus-info__note--muted {
  font-size: 0.8125rem;
  color: #a1a1aa;
  font-style: italic;
  margin: 0;
}

.opus-info__time-display {
  margin-top: 0.5rem;
}

.opus-info__time-badge {
  display: inline-block;
  background-color: #fef3c7;
  border: 1px solid #fde047;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #78350f;
}

.opus-info__time-hours {
  margin-left: 0.5rem;
  font-size: 0.875rem;
  color: #78350f;
  font-weight: 500;
}

.opus-info__date-display {
  margin-top: 0.5rem;
}

.opus-info__date-text {
  font-size: 0.875rem;
  color: #78350f;
  margin-bottom: 0.5rem;
}

.opus-info__date-days {
  color: #92400e;
}

.opus-info__toggle-row--secondary {
  margin-top: 0.5rem;
}

.opus-info__reason-display {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.opus-info__reason-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #78350f;
}

.opus-info__reason-code {
  font-size: 0.75rem;
  color: #92400e;
}

.opus-info__extra-info {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px dashed #fde047;
}

.opus-info__label--secondary {
  font-size: 0.6875rem;
  margin-bottom: 0.375rem;
}

.opus-info__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #dc2626;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
}

.opus-info__error-icon {
  font-size: 1rem;
}

.opus-info__notice {
  font-size: 0.8125rem;
  color: #78350f;
  background-color: #fef3c7;
  border: 1px solid #fde047;
  border-radius: 4px;
  padding: 0.625rem 0.75rem;
  margin-top: 1rem;
}

.opus-info__notice--warning {
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.error-message {
  color: #ff3860;
  font-size: 11px;
  font-weight: 400;
  padding: 1px 2px 2px;
}

.create-btn {
  width: 200px;
  height: 38px;
  margin-left: 10px;
  color: white;
  box-shadow: 0 2px 2px rgba(0, 0, 0, .1);
  background-color: var(--blue);
  border: 1px solid #4e80b7 !important;
  transition: background-color 0.3s, transform 0.3s;
  transform: scale(1);
}

.create-btn:hover {
  background-color: #66a1e4;
}

.create-btn:active {
  transform: scale(0.98)
}

.create__form-group {
  margin-bottom: 15px;
}

.absence-reason {
  display: flex;
  align-items: center;
  position: relative;
}

.create-absence-link {
  background-color: var(--blue);
  color: white;
  cursor: pointer;
}

.create__form-group label {
  letter-spacing: .075rem;
  text-transform: uppercase;
  font-size: .8rem;
  line-height: 1rem;
  margin-bottom: .5rem;
  font-weight: 700;
  display: block;
  color: #a2a3a6;
}

textarea.d-input {
  background-color: #ffffff;
}

.create__form-group__note-to-absence textarea.d-input {
  box-shadow: none;
  border: 1px solid #d8d8d8;
  background: transparent;
}

.create__form-group__note-to-absence textarea.d-input:focus, input.d-input:focus {
  border-color: var(--blue);
  background: #ffffff;
  box-shadow: 0 2px 5px -1px rgb(50 50 93 / 25%), 0 1px 3px -1px rgb(0 0 0 / 30%);
}

.absence-no-end-date {
  display: flex;
  align-items: center;
  user-select: none;
  cursor: pointer;
}

.absence-no-end-date.absence-no-end-date--active label {
  color: var(--blue);
}

.absence-no-end-date .text-explainer {
  font-size: .8rem;
  line-height: 1rem;
  font-weight: 700;
  display: block;
  color: #a2a3a6;
}

.absence-no-end-date label {
  margin-bottom: 0;
  margin-left: 0.5rem;
  margin-right: 0.5rem;
  cursor: pointer;
}

.absence-no-end-date__inner {
  display: flex;
  align-items: center;
}

.time-changer__group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.time-changer__settings {
  color: rgb(162, 163, 166);
}

.time-changer__settings:hover {
  color: var(--dark-blue);
}


.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  transform: scale(0.8);
}

.create__form-group__header {
  display: flex;
  justify-content: space-between;
}

.d-icon-plus {
  font-size: 13px;
}

.d-icon-plus:before {
  content: '+';
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 14px;
  height: 14px;
  background-color: rgb(162, 163, 166);
  opacity: 0.6;
}

.absence-reasons__settings {
  position: absolute;
  top: 2.5px;
  right: -28px;
}

.absence-reasons__settings.icon__bg-circle:hover .d-icon-plus:before {
  opacity: 1;
  background-color: var(--blue);
}

.do-opus-badge,
.do-sd-badge {
  display: inline-flex;
  font-weight: 400;
  border: 1px solid #facc17;
  border-radius: 4px;
  background-color: #fefce8;
  padding: 3px 4px;
  border-radius: 4px;
  font-size: 9px;
  line-height: 1.5;
  color: #a26206;
}

.users-employment-selection .do-select {
  width: 100%;
}

.salary-integration-warning {
  margin-bottom: 15px;
}

.salary-integration-warning .do-opus-badge {
  display: inline-block;
}

.d-input-group-flex {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  height: 34px;
  padding-left: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
}

.d-input-group-flex label {
  margin-bottom: 0;
}

.d-input--flex {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.create__form-group__salart-system-info {
  border: 1px solid #facc17;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background-color: #fefce8;
  margin-bottom: 15px;
}

.do-opus-whole-day-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.date-picker-range-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.create__form-group__flex-container {
  display: flex;
  flex-direction: row;
  gap: 1rem;
  flex-wrap: wrap;
}

.create__form-group__flex-container > .create__form-group {
  flex: 1;
}

.create-absence__form--flex {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: 100%;
}

.create-absence__form--flex > .create__form-group {
  flex: 1;
}

.opus-info__header {
  position: absolute;
  top: 1rem;
  right: 1rem;
}

</style>



// WEBPACK FOOTER //
// SubsCreate.vue?2bca689a